import { ImmerReducer } from 'immer-reducer';
import { clamp } from 'lodash-es';

import type { CategoryType, IndustryType, ServiceType } from 'components/OnlineBookingSettings/types/types';

import { TimeSlot } from '../api/api';
import { HOMEOWNER_FLOW_FIRST_STEP, HOMEOWNER_FLOW_LAST_STEP } from '../Steps/consts';

import type { ContactDetailsValues, State } from './types';

const DEFAULT_COUNTRY = 'United States';

export const initialState: State = {
  previousStep: HOMEOWNER_FLOW_FIRST_STEP,
  step: HOMEOWNER_FLOW_FIRST_STEP,
  industry: undefined,
  category: undefined,
  service: undefined,
  description: '',
  question1: null,
  question2: null,
  contactDetails: {
    submitting: false,
    valid: false,
    values: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      country: DEFAULT_COUNTRY,
      addressLine1: '',
      addressLine2: '',
      state: '',
      city: '',
      zipCode: '',
    },
  },
  orgUuid: '',
  success: false,
  unavailableDateError: false,
  arrivalWindow: {
    date: undefined,
    time: undefined,
  },
};

export class OnlineBookingReducer extends ImmerReducer<State> {
  nextStep() {
    this.updateStepBy(1);
  }

  previousStep() {
    this.updateStepBy(-1);
  }

  resetStep() {
    this.draftState.previousStep = HOMEOWNER_FLOW_FIRST_STEP;
    this.draftState.step = HOMEOWNER_FLOW_FIRST_STEP;
  }

  updateStepBy(delta: number) {
    this.draftState.previousStep = this.draftState.step;
    this.draftState.step = clamp(this.draftState.step + delta, HOMEOWNER_FLOW_FIRST_STEP, HOMEOWNER_FLOW_LAST_STEP);
  }

  skipStep() {
    if (this.draftState.previousStep >= this.draftState.step) {
      this.previousStep();
      return;
    }

    this.nextStep();
  }

  selectIndustry(industry: IndustryType) {
    this.draftState.industry = industry;
  }

  selectCategory(category: CategoryType) {
    this.draftState.category = category;
  }

  selectService(service: ServiceType) {
    this.draftState.service = service;
  }

  updateDescription(description: string) {
    this.draftState.description = description;
  }

  updateQuestionOne(value: string) {
    this.draftState.question1 = value;
  }

  updateQuestionTwo(value: string) {
    this.draftState.question2 = value;
  }

  updateContactDetailsValues(values: ContactDetailsValues) {
    this.draftState.contactDetails.values = values;
  }

  updateContactDetailsValid(valid: boolean) {
    this.draftState.contactDetails.valid = valid;
  }

  startSubmittingContactDetailsForm() {
    this.draftState.contactDetails.submitting = true;
  }

  stopSubmittingContactDetailsForm() {
    this.draftState.contactDetails.submitting = false;
  }

  setSuccess() {
    this.draftState.success = true;
  }

  selectArrivalDate(date: string) {
    this.draftState.arrivalWindow.date = date;
  }

  selectArrivalTime(time: TimeSlot) {
    this.draftState.arrivalWindow.time = time;
  }

  resetArrivalTime() {
    this.draftState.arrivalWindow.time = undefined;
  }

  setUnavailableDateError(isError: boolean) {
    this.draftState.unavailableDateError = isError;
  }
}
